import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { installAutoErrorToast } from '../shared/utils/apiError'
import { getAccessToken, clearAccessToken, refreshAccessToken } from '../shared/auth/token'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

// `skipErrorToast` itself is declared globally by shared/utils/apiError.ts's
// module augmentation, alongside installAutoErrorToast which reads it.
type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      // GoodDay is namespaced so this client never reaches Jira/Zynx routes.
      baseURL: `${API_URL}/api/v1/goodday`,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    })

    this.client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = getAccessToken()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
      if (config.data instanceof FormData && config.headers) {
        delete config.headers['Content-Type']
      }
      return config
    })

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as RetriableRequestConfig | undefined
        // Only exclude the endpoints where a 401 is a real auth failure, not
        // an expired access token — a bad login/register/refresh should not
        // trigger another refresh attempt. /auth/me and /auth/logout still
        // go through the normal refresh-and-retry path below.
        const isAuthUrl = /\/auth\/(login|register|refresh)$/.test(originalRequest?.url || '')

        // Access tokens are short-lived (15m); on a 401 (other than from the
        // auth endpoints themselves), silently exchange the httpOnly refresh
        // cookie for a new access token and retry, instead of forcing a full
        // re-login every time the token expires.
        if (error.response?.status === 401 && originalRequest && !isAuthUrl && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            const newToken = await refreshAccessToken()
            originalRequest.headers = originalRequest.headers || ({} as any)
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return this.client(originalRequest)
          } catch {
            this.redirectToLogin()
            return Promise.reject(error)
          }
        }

        if (error.response?.status === 401 && !isAuthUrl) {
          this.redirectToLogin()
        }

        return Promise.reject(error)
      }
    )

    // Shared with the Spaces API client — see installAutoErrorToast's doc
    // comment for the one rule every client in this app follows.
    installAutoErrorToast(this.client)
  }

  /** The underlying axios instance (auth header + token refresh included), for callers that need full responses. */
  get raw(): AxiosInstance {
    return this.client
  }

  private redirectToLogin() {
    clearAccessToken()
    const isAuthPage = typeof window !== 'undefined' && (
      window.location.pathname === '/login' ||
      window.location.pathname === '/register' ||
      window.location.pathname === '/'
    )
    if (!isAuthPage && typeof window !== 'undefined') {
      window.location.href = '/login'
    }
  }

  async get<T>(url: string, params?: Record<string, any>, config?: any) {
    const response = await this.client.get<T>(url, { params, ...config })
    return response.data
  }

  async post<T>(url: string, data?: any, config?: any) {
    const response = await this.client.post<T>(url, data, config)
    return response.data
  }

  async patch<T>(url: string, data?: any, config?: any) {
    const response = await this.client.patch<T>(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: any, config?: any) {
    const response = await this.client.put<T>(url, data, config)
    return response.data
  }

  async delete<T>(url: string, config?: any) {
    const response = await this.client.delete<T>(url, {
      headers: { 'Content-Type': undefined },
      ...config,
    })
    return response.data
  }
}

export const api = new ApiClient()
